/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Message } from 'primereact/message';
import { formatTimestamp } from 'sharedUi/dateUtils';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { ActionButton, PageSkeleton, StatusChip } from '../../components/common/panel';
import { Icon } from '../../components/common/icon';
import { useCanUpdate, useHasPermission } from '../../hooks/usePermissions';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useUserAuditNames } from '../../hooks/useUserLookup';
import { JOB_NAMES } from '../../constants/jobNames';
import { PERMISSIONS } from '../../constants/permissions';
import { agentsService } from '../../services/agents.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import { extractErrorMessage } from '../../utils/error-handler';
import type { Agent, EvalTool } from '../../types';
import {
  AgentConversationsPanel,
  AgentDependenciesPanel,
  AgentObservabilityPanel,
  AgentPerformancePanel,
} from '../../components/agents/agent-insight-panels';

const encodeId = (id: string): string => {
  try {
    return btoa(id).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  } catch {
    return id;
  }
};

type ParamRow = { name: string; type: string; required: boolean; description?: string; enumValues?: string[] };

/** Flatten a tool's JSON-Schema `parameters` into readable rows for display. */
const paramRows = (parameters?: Record<string, unknown>): ParamRow[] => {
  if (!parameters || typeof parameters !== 'object') return [];
  const props = (parameters as any).properties as Record<string, any> | undefined;
  if (!props || typeof props !== 'object') return [];
  const required: string[] = Array.isArray((parameters as any).required) ? (parameters as any).required : [];
  return Object.entries(props).map(([name, raw]) => {
    const s = (raw || {}) as any;
    let type = typeof s.type === 'string' ? s.type : 'any';
    if (type === 'array' && s.items && typeof s.items.type === 'string') type = `${s.items.type}[]`;
    return {
      name, type, required: required.includes(name),
      description: typeof s.description === 'string' ? s.description : undefined,
      enumValues: Array.isArray(s.enum) ? s.enum.map(String) : undefined,
    };
  });
};

/** A deterministic gradient for the agent avatar tile. */
const avatarGradient = (key: string) => {
  const grads = [
    'linear-gradient(135deg, #6366f1, #4338ca)', 'linear-gradient(135deg, #0ea5e9, #0369a1)',
    'linear-gradient(135deg, #10b981, #047857)', 'linear-gradient(135deg, #f59e0b, #b45309)',
    'linear-gradient(135deg, #ec4899, #be185d)', 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
  ];
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return grads[h % grads.length];
};

const AgentView = () => {
  const { id, navigateToEdit, navigateToList, goBack } = useSecureNavigation({ entity: 'agent', basePath: '/agents' });
  // Back returns to where you came from (the list when you came from the list, a
  // RAG/prompt when you came from a cross-link), via browser history.
  const smartBack = () => (((window.history.state as any)?.idx ?? 0) > 0 ? goBack() : navigateToList());
  const canUpdate = useCanUpdate(JOB_NAMES.AGENT);
  const canReadExec = useHasPermission(PERMISSIONS.PROMPT_EXECUTION_READ);
  const [activeSection, setActiveSection] = useState('');

  const [projects, setProjects] = useState<AccessibleProject[]>([]);
  useEffect(() => { projectsService.getAccessible().then(setProjects).catch(() => setProjects([])); }, []);

  const { data: agent, isLoading, error } = useQuery({
    queryKey: ['agent', 'detail', id], queryFn: () => agentsService.getById(id!), enabled: !!id, staleTime: 3e5,
  });
  const { data: history = [] } = useQuery({
    queryKey: ['agent', 'versions', id], queryFn: () => agentsService.listVersions(id!), enabled: !!id, staleTime: 6e4,
  });
  const { data: overview } = useQuery({
    queryKey: ['agent-overview', id], queryFn: () => agentsService.getOverview(id!), enabled: !!id, staleTime: 3e4,
  });
  const { createdByName, updatedByName } = useUserAuditNames((agent as any)?.createdBy, (agent as any)?.updatedBy ?? undefined);

  // Scrollspy: highlight the section in view once the agent (and anchors) load.
  useEffect(() => {
    if (!agent) return;
    const els = Array.from(document.querySelectorAll('[id^="sec-"]')) as HTMLElement[];
    if (!els.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveSection(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -60% 0px' },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [agent?._id]);

  if (isLoading) return <PageSkeleton />;
  if (error || !agent) {
    return (
      <div className="p-4">
        <Message severity="error" text={error ? extractErrorMessage(error, 'Failed to load agent') : 'Agent not found'} className="mb-4 w-full" />
        <ActionButton kind="secondary" icon="arrow-left" label="Back to Agents" onClick={() => navigateToList()} />
      </div>
    );
  }

  const a: Agent = agent;
  const tools: EvalTool[] = a.tools || [];
  const conv = (a as any).conversation as any;
  // The saved snapshots are PRIOR definitions; the live version isn't among
  // them. Show it as the top entry so an agent on v2 never reads "(0) versions".
  const currentVersion = {
    _id: 'current',
    version: a.version ?? 1,
    isCurrent: true,
    createdAt: a.updatedAt || a.createdAt,
    definition: { promptRef: a.promptRef, promptVersion: a.promptVersion, tools: a.tools, systemPrompt: a.systemPrompt },
  };
  const allVersions = [currentVersion, ...history].filter(
    (v, i, arr) => arr.findIndex((x) => x.version === v.version) === i,
  );
  const toolChoice = a.toolChoice?.mode && a.toolChoice.mode !== 'AUTO'
    ? `${a.toolChoice.mode}${a.toolChoice.toolName ? ` · ${a.toolChoice.toolName}` : ''}` : 'Auto';
  const projectName = (appId?: string | null): string => {
    if (!appId) return 'Global (all projects)';
    const p = projects.find((x) => x._id === String(appId));
    return p ? (p.appCode ? `${p.name} (${p.appCode})` : p.name) : String(appId);
  };
  const stamp = (at: unknown, by: string) => {
    const when = at ? formatTimestamp(at as string) : '';
    const who = by && by !== '-' ? by : '';
    if (!when && !who) return '–';
    return (<>{when && <span className="tabular-nums">{when}</span>}{who && <span className="block text-xs text-ink-muted">{who}</span>}</>);
  };

  const scrollToId = (sid: string) => {
    const el = document.getElementById(sid);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(sid);
    el.classList.add('ring-2', 'ring-blue-400', 'ring-offset-2');
    setTimeout(() => el.classList.remove('ring-2', 'ring-blue-400', 'ring-offset-2'), 1200);
  };

  const navItems = [
    a.systemPrompt && { id: 'sec-system-prompt', label: 'System prompt' },
    { id: 'sec-tools', label: `Tools (${tools.length})` },
    conv?.enabled && { id: 'sec-conversation', label: 'Conversation' },
    a.modelParams && Object.keys(a.modelParams).length > 0 && { id: 'sec-model-params', label: 'Model parameters' },
    overview?.onlineEval?.enabled && { id: 'sec-online-eval', label: 'Online eval' },
    { id: 'sec-dependencies', label: 'Dependencies' },
    { id: 'sec-performance', label: 'Performance' },
    { id: 'sec-conversations', label: 'Conversations' },
    canReadExec && { id: 'sec-observability', label: 'Observability' },
    { id: 'sec-versions', label: `Version history (${allVersions.length})` },
  ].filter(Boolean) as { id: string; label: string }[];

  return (
    <PermissionGuard jobName={JOB_NAMES.AGENT}>
      <div className="mx-auto max-w-[1280px] px-3 pb-10 pt-2 sm:px-5" data-testid="SCR-Agent-View">
        <button type="button" onClick={smartBack} className="pd-back ui-press">
          <Icon name="arrow-left" size={14} /> Agents
        </button>

        {/* Header */}
        <header className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-3.5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm ring-2 ring-white dark:ring-gray-800"
              style={{ background: avatarGradient(a.agentCode || a.name || '') }}>
              <i className="pi pi-android" style={{ fontSize: '1.5rem' }} />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-title font-semibold text-ink">{a.name}</h1>
                <StatusChip tone={a.status === 'ACTIVE' ? 'success' : 'neutral'}>{a.status || 'ACTIVE'}</StatusChip>
                <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-600 dark:bg-gray-700 dark:text-gray-300">v{a.version ?? 1}</span>
              </div>
              <p className="mt-0.5 font-mono text-xs text-indigo-600 dark:text-indigo-400">{a.agentCode}</p>
              <p className="mt-1 max-w-3xl text-sm text-ink-muted">
                {a.description || <span className="italic text-gray-400">No description</span>}
              </p>
            </div>
          </div>
          {canUpdate && id && <ActionButton kind="secondary" icon="pencil" label="Edit" onClick={() => navigateToEdit(id)} />}
        </header>

        <div className="mt-6 grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_21rem]">
          {/* Main column */}
          <div className="flex min-w-0 flex-col gap-4">
            {a.systemPrompt && (
              <section id="sec-system-prompt" className="ui-panel p-5 scroll-mt-20">
                <h2 className="text-sm font-semibold text-ink">System prompt</h2>
                <pre className="ui-inset pd-well font-sans mt-3 whitespace-pre-wrap">{a.systemPrompt}</pre>
              </section>
            )}

            {/* Tools with full parameter schemas. */}
            <section id="sec-tools" className="ui-panel p-5 scroll-mt-20">
              <h2 className="text-sm font-semibold text-ink">Tools <span className="font-normal text-ink-muted">({tools.length})</span></h2>
              {tools.length === 0 ? (
                <p className="mt-3 text-sm text-ink-muted">No tools — this agent answers without any.</p>
              ) : (
                <ul className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                  {tools.map((t, i) => {
                    const rows = paramRows(t.parameters);
                    return (
                      <li key={`${t.name}-${i}`} className="rounded-lg border border-line bg-gray-50 p-3 dark:bg-gray-800/40">
                        <div className="flex items-center gap-2">
                          <i className="pi pi-bolt text-indigo-500" style={{ fontSize: '0.75rem' }} />
                          <span className="font-mono text-sm font-medium text-ink">{t.name}</span>
                          <span className="ml-auto rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500 dark:bg-gray-700 dark:text-gray-300">
                            {rows.length} param{rows.length === 1 ? '' : 's'}
                          </span>
                        </div>
                        {t.description && <p className="mt-1 text-xs text-ink-muted">{t.description}</p>}
                        {rows.length > 0 && (
                          <ul className="mt-2 flex flex-col gap-1.5 border-t border-line pt-2">
                            {rows.map((r) => (
                              <li key={r.name} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                <span className="font-mono text-xs text-slate-700 dark:text-slate-200">{r.name}</span>
                                <span className="rounded bg-indigo-50 px-1 py-0.5 font-mono text-[10px] text-indigo-600 dark:bg-gray-800 dark:text-indigo-300">{r.type}</span>
                                {r.required && <span className="rounded bg-rose-50 px-1 py-0.5 text-[10px] font-medium text-rose-600 dark:bg-rose-900/30 dark:text-rose-300">required</span>}
                                {r.description && <span className="text-[11px] text-ink-muted">{r.description}</span>}
                                {r.enumValues && r.enumValues.length > 0 && (
                                  <span className="flex flex-wrap gap-1">
                                    {r.enumValues.map((v) => (
                                      <span key={v} className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[10px] text-slate-600 dark:bg-gray-800 dark:text-slate-300">{v}</span>
                                    ))}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {/* Conversation — only for a chat-enabled agent. */}
            {conv?.enabled && (
              <section id="sec-conversation" className="ui-panel p-5 scroll-mt-20">
                <h2 className="text-sm font-semibold text-ink">Conversation</h2>
                <p className="mt-0.5 text-xs text-ink-muted">How this agent behaves as a chat.</p>
                <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4 lg:grid-cols-4">
                  <div><span className="pd-label">History sent</span><p className="pd-text">{conv.historyMessages != null ? `${conv.historyMessages} msgs` : '—'}</p></div>
                  <div><span className="pd-label">Rewrite window</span><p className="pd-text">{conv.rewriteQuery ? (conv.rewriteWindow != null ? `${conv.rewriteWindow} msgs` : 'On') : 'Off'}</p></div>
                  <div><span className="pd-label">Summarize</span><p className="pd-text">{conv.summarize ? (conv.maxSummaryChars != null ? `On · ≤${conv.maxSummaryChars} chars` : 'On') : 'Off'}</p></div>
                  <div><span className="pd-label">Max tokens</span><p className="pd-text">{conv.maxTokens != null ? conv.maxTokens.toLocaleString() : '—'}</p></div>
                </div>
              </section>
            )}

            {/* Model parameters. */}
            {a.modelParams && Object.keys(a.modelParams).length > 0 && (
              <section id="sec-model-params" className="ui-panel p-5 scroll-mt-20">
                <h2 className="text-sm font-semibold text-ink">Model parameters</h2>
                <pre className="ui-inset pd-well pd-well--mono mt-3 max-h-64 overflow-auto">{JSON.stringify(a.modelParams, null, 2)}</pre>
              </section>
            )}

            {/* Online eval. */}
            {overview?.onlineEval?.enabled && (
              <section id="sec-online-eval" className="ui-panel p-5 scroll-mt-20">
                <h2 className="text-sm font-semibold text-ink">Online eval</h2>
                <p className="mt-0.5 text-xs text-ink-muted">A sample of live turns is scored by a judge model.</p>
                <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4 lg:grid-cols-3">
                  <div><span className="pd-label">Sample rate</span><p className="pd-text">{overview.onlineEval.sampleRate != null ? `${Math.round((overview.onlineEval.sampleRate || 0) * 100)}%` : '—'}</p></div>
                  <div><span className="pd-label">Judge model</span><p className="pd-text">{overview.onlineEval.judgeModelName || overview.onlineEval.judgeModelRef || 'Default'}</p></div>
                  <div><span className="pd-label">Metrics</span><p className="pd-text">{(overview.onlineEval.metrics && overview.onlineEval.metrics.length) ? overview.onlineEval.metrics.join(', ') : '—'}</p></div>
                </div>
              </section>
            )}

            {/* Dependency layer — both directions. */}
            {id && <div id="sec-dependencies" className="scroll-mt-20"><AgentDependenciesPanel agentId={id} overview={overview} /></div>}

            {/* Performance — response time, feedback, eval quality. */}
            {id && <div id="sec-performance" className="scroll-mt-20"><AgentPerformancePanel agentId={id} /></div>}

            {/* Conversations — the sessions running on this agent. */}
            {id && <div id="sec-conversations" className="scroll-mt-20"><AgentConversationsPanel agentId={id} /></div>}

            {/* Observability — the agent's traces (runs + health). */}
            {id && canReadExec && a.agentCode && (
              <div id="sec-observability" className="scroll-mt-20"><AgentObservabilityPanel agentCode={a.agentCode} /></div>
            )}

            {/* Version history: the current definition, then any saved snapshots. */}
            <section id="sec-versions" className="ui-panel p-5 scroll-mt-20" data-testid="AgentView.Versions">
              <h2 className="text-sm font-semibold text-ink">Version history <span className="font-normal text-ink-muted">({allVersions.length})</span></h2>
              <ol className="mt-3 flex flex-col gap-2">
                {allVersions.map((h: any) => (
                  <li key={h._id} className="rounded-lg border border-line p-3">
                    <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
                      <span className="font-semibold tabular-nums text-ink">v{h.version}</span>
                      {h.isCurrent && <StatusChip tone="success" dot={false}>Current</StatusChip>}
                      {h.definition.promptRef && (
                        <span className="text-xs text-ink-muted">prompt {h.definition.promptVersion ? `v${h.definition.promptVersion}` : 'active'}</span>
                      )}
                      <span className="text-xs text-ink-muted">{(h.definition.tools || []).length} tools</span>
                      <span className="ml-auto text-xs text-ink-muted">{h.createdAt ? formatTimestamp(h.createdAt) : '—'}</span>
                    </div>
                    {h.definition.systemPrompt && (
                      <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">{h.definition.systemPrompt}</p>
                    )}
                  </li>
                ))}
              </ol>
              {history.length === 0 && (
                <p className="mt-3 text-xs text-ink-muted">Only the live definition is shown — prior snapshots start saving with the next change.</p>
              )}
            </section>
          </div>

          {/* Side rail */}
          <aside className="ui-panel p-5 lg:sticky lg:top-4">
            <h2 className="text-sm font-semibold text-ink">Details</h2>
            <dl className="pd-meta mt-4">
              <dt>Code</dt><dd className="font-mono text-xs">{a.agentCode}</dd>
              <dt>Status</dt><dd><StatusChip tone={a.status === 'ACTIVE' ? 'success' : 'neutral'}>{a.status || 'ACTIVE'}</StatusChip></dd>
              <dt>Tool choice</dt><dd>{toolChoice}</dd>
              <dt>Tools</dt><dd className="tabular-nums">{tools.length}</dd>
              <dt>Chat</dt><dd>{conv?.enabled ? 'Enabled' : 'Off'}</dd>
              <dt>Project</dt>
              <dd>
                {a.appId
                  ? <Link to={`/projects/${encodeId(String(a.appId))}`} className="text-blue-600 hover:underline">{projectName(a.appId)}</Link>
                  : projectName(a.appId)}
              </dd>
              {a.tags && a.tags.length > 0 && (
                <>
                  <dt>Tags</dt>
                  <dd><span className="flex flex-wrap gap-1">{a.tags.map((t) => <StatusChip key={t} tone="neutral" dot={false}>{t}</StatusChip>)}</span></dd>
                </>
              )}
            </dl>
            <hr className="pd-rule my-5" />
            <dl className="pd-meta">
              <dt>Created</dt><dd>{stamp(a.createdAt, createdByName)}</dd>
              <dt>Updated</dt><dd>{stamp(a.updatedAt, updatedByName)}</dd>
            </dl>

            {navItems.length > 0 && (
              <>
                <hr className="pd-rule my-5" />
                <nav aria-label="Agent sections">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">Agent sections</h3>
                  <ul className="flex flex-col gap-0.5">
                    {navItems.map((it) => {
                      const activeNav = activeSection === it.id;
                      return (
                        <li key={it.id}>
                          <button type="button" onClick={() => scrollToId(it.id)}
                            aria-current={activeNav ? 'true' : undefined}
                            className={`flex w-full items-center gap-1.5 border-l-2 py-1 pl-2 pr-2 text-left text-sm transition-colors ${
                              activeNav ? 'border-blue-600 font-medium text-blue-600'
                                : 'border-transparent text-ink-muted hover:border-gray-300 hover:text-blue-600'
                            }`}>
                            <span className="text-xs opacity-60">›</span>
                            {it.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </nav>
              </>
            )}
          </aside>
        </div>
      </div>
    </PermissionGuard>
  );
};

export default AgentView;
